// shared/src/prompts/manifest.ts
// Replay-correlation manifest builder for f.1.finalize PromptsPayload.
// Imports every prompt module's VERSION/STEP/MODEL/MAX_TOKENS at build time
// and returns the immutable snapshot.
//
// Added 2026-05-22 per architectural-audit follow-up #C-01 + #L-01 close —
// replaces the previous `payload: {}` stub at chain-workflow/.../f.1.finalize.ts:71
// that violated [[feedback-no-stubs]].
// 2026-05-24 — AS-4 added q.1.qa (qa-worker prompt module) to the roster.
// 2026-05-25 — Phase 6 §C cutover: V1 prompt modules deleted. Roster now
// covers chain V2 prompts: 7 market (contrarian/consensus × analyst/skeptic/refine
// + synthesis) + inductive + 7 per-ticker (contrarian/consensus × analyst/skeptic/refine
// + arbiter-v2) + a.1.synthesis + q.1.qa. p.3.momentum dropped per design L22.

import * as MContrarianAnalyst from './m-contrarian-analyst.js';
import * as MContrarianSkeptic from './m-contrarian-skeptic.js';
import * as MContrarianRefine  from './m-contrarian-refine.js';
import * as MConsensusAnalyst  from './m-consensus-analyst.js';
import * as MConsensusSkeptic  from './m-consensus-skeptic.js';
import * as MConsensusRefine   from './m-consensus-refine.js';
import * as MSynthesis       from './m-synthesis.js';
import * as MInductiveForecast from './m-inductive-forecast.js';
import * as PContrarianAnalyst from './p-contrarian-analyst.js';
import * as PContrarianSkeptic from './p-contrarian-skeptic.js';
import * as PContrarianRefine  from './p-contrarian-refine.js';
import * as PConsensusAnalyst  from './p-consensus-analyst.js';
import * as PConsensusSkeptic  from './p-consensus-skeptic.js';
import * as PConsensusRefine   from './p-consensus-refine.js';
import * as PArbiter         from './p-arbiter.js';
import * as PInductiveForecast from './p-inductive-forecast.js';
import * as ASynthesis         from './a-synthesis.js';
import * as QQa                from './q-qa.js';
import * as SynthesisQualityCrux from './synthesis-quality-crux.js';
import * as QQaJudge           from './q-qa-judge.js';
import * as QRankProbe          from './q-rank-probe.js';
import * as LhtThesis           from './lht-thesis.js';

import type { PromptManifestEntry } from '../types/brief-payload.js';
import type { DeepseekModel } from '../pricing.js';

export interface PromptModule {
  STEP:       string;
  VERSION:    string;
  MODEL:      string;   // per-module `as const` literal; cast at the boundary below
  MAX_TOKENS: number;
  ROLE:       string;   // per-module `as const` role (analyst/skeptic/refine/synthesis/…); #45 registry reads it
  DESC:       string;   // per-module `as const` one-line operator description; #45 registry column
}

// Exported (2026-07-10, #45) so the LLM-step registry (prompts/registry.ts) can
// join this authoritative module roster with the design-lock tier table without
// re-importing all 22 modules. buildPromptManifest() below is unchanged.
export const PROMPT_MODULES: ReadonlyArray<PromptModule> = [
  // Market path V2 (6 path prompts + 1 synthesis + 1 inductive)
  MContrarianAnalyst, MContrarianSkeptic, MContrarianRefine,
  MConsensusAnalyst,  MConsensusSkeptic,  MConsensusRefine,
  MSynthesis,
  MInductiveForecast,
  // Per-ticker path V2 (6 path prompts + 1 arbiter + 1 inductive)
  PContrarianAnalyst, PContrarianSkeptic, PContrarianRefine,
  PConsensusAnalyst,  PConsensusSkeptic,  PConsensusRefine,
  PArbiter,
  PInductiveForecast,
  // Basket + Q&A
  ASynthesis,
  QQa,
  // Post-horizon scorers
  SynthesisQualityCrux,                  // Bundle C Phase 2 2026-05-27 (macro crux)
  QQaJudge,                              // Q&A Calibration 2026-06-16 (qualitative Q&A judge)
  // Calibration probes
  QRankProbe,                            // Cross-sectional rank-IC probe 2026-06-25 (enrolled audit 2026-06-29-001 DC-004)
  // Long-Horizon Thesis narration 2026-07-09 (macro-prediction-scorer; the one LHT LLM step)
  LhtThesis,
];

/**
 * Returns the immutable manifest snapshot of all chain-V2 prompt modules.
 * Call once per F-stage emit to capture the prompt contract that produced
 * the brief's signals.
 */
export function buildPromptManifest(): PromptManifestEntry[] {
  return PROMPT_MODULES.map((m) => ({
    step:       m.STEP,
    version:    m.VERSION,
    model:      m.MODEL as DeepseekModel,
    max_tokens: m.MAX_TOKENS,
  }));
}
